import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import {
  MOMENT_TYPES, blankMoment, applyAllSmartDefaults,
  parsePasteText, parseMatchImportFile, detectDuplicateMoments, detectDuplicateRoleReviews,
  reconcileMomentImports, inferMomentType, createTemplateWorkbookBlob, perspectiveLabel
} from '../utils/matchImport';
import {
  Loader2, AlertCircle, ChevronLeft, Plus, Trash2, ChevronDown, ChevronUp, Copy, Film, Upload, X, Play, RefreshCw
} from 'lucide-react';

function downloadTemplate() {
  const blob = createTemplateWorkbookBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'tactmark-template.xlsx'; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const TACTICAL_FOCUS_OPTIONS = [
  'Defensive transitions','Pressing structure','Build-up play','Final third decisions',
  'Set pieces','Game management','Opponent scouting','General review'
];
const CONTRIBUTION_OPTIONS  = ['Strong','Acceptable','Weak','Not visible','Not applicable'];
const PHASE_OF_PLAY_OPTIONS = ['Build-up','Defensive transition','Attacking transition','Set piece','High press','Counter-attack','Crossing','Open play'];
const TAG_OPTIONS           = ['goal','error','chance','turnover','tactical','set piece','counter'];

const STEPS = ['Match Details','Key Moments','Generate Report'];
const DRAFT_KEY = 'tactmark_analysis_draft';

const PERSPECTIVE_OPTIONS = [
  { value: 'for_us', label: 'For us' },
  { value: 'against_us', label: 'Against us' },
  { value: 'neutral', label: 'Neutral' },
];

function inputCls() {
  return 'w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-navy-900 placeholder-gray-400 focus:outline-none focus:border-navy-600';
}
function smallInputCls() {
  return 'bg-white border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-navy-900 placeholder-gray-400 focus:outline-none focus:border-navy-600';
}

function blankRoleReview() {
  return {
    player_name:'',position_or_role:'',minutes_played:'',
    involvement_level:'Normal',physical_condition:'Fit',fatigue_concern:'None',
    pressing_contribution:'Acceptable',attacking_contribution:'Acceptable',
    defensive_contribution:'Acceptable',coach_concern:'',should_review:false,notes:'',
    positive_contribution:'',main_concern:'',repeated_issue:'',training_focus:'',
    confidence_level:'Medium',decision_question:''
  };
}

// Stable client-side ID for retry-idempotent moment and role-review sync.
// Uses crypto.randomUUID when available (all modern browsers), falls back to Math.random.
function genClientId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'cid-' + Math.random().toString(36).slice(2) + '-' + Date.now().toString(36);
}

// Ensure every moment in an array has a stable client_input_id.
// Assigns one once and never replaces an existing ID.
function ensureClientIds(moments) {
  return moments.map(m => m && m.client_input_id ? m : { ...m, client_input_id: genClientId() });
}

function computeReadiness(moments) {
  const nonBlank = (moments||[]).filter(m => {
    if (!m) return false;
    const hasMin  = m.minute && String(m.minute).trim();
    const hasDesc = m.what_happened && String(m.what_happened).trim();
    const hasPers = m.perspective && String(m.perspective).trim();
    return hasMin || hasDesc || hasPers || (m.moment_type && String(m.moment_type).trim());
  });
  if (nonBlank.length === 0) return { state:'none', count:0, incompleteCount:0, incompleteIndexes:[] };
  const incomplete = nonBlank
    .map((m,i) => ({ m, origIdx:i }))
    .filter(({ m }) => !m.minute || !m.what_happened || !m.perspective);
  if (incomplete.length === 0) return { state:'ready', count:nonBlank.length, incompleteCount:0, incompleteIndexes:[] };
  return { state:'needs_review', count:nonBlank.length, incompleteCount:incomplete.length, incompleteIndexes:incomplete.map(({ origIdx }) => origIdx) };
}

export default function UploadMatch() {
  const navigate = useNavigate();

  // ── Core state ──────────────────────────────────────────────────────────────
  const [step, setStep]       = useState(0);
  const [maxStep, setMaxStep] = useState(0);
  const [isDirty, setIsDirty] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]     = useState('');
  const [saveMsg, setSaveMsg] = useState('');
  const [validationIssues, setValidationIssues] = useState(null);
  const [importResult, setImportResult] = useState(null); // { added, merged, excluded, conflicts, prevMoments, prevRoleReviews }

  // ── Teams ────────────────────────────────────────────────────────────────────
  const [teams, setTeams]           = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(true);

  // ── Match ────────────────────────────────────────────────────────────────────
  const [match, setMatch] = useState({
    teamId:'',teamName:'',opponent:'',date:new Date().toISOString().split('T')[0],
    competition:'',ourScore:'',opponentScore:'',
    teamFormation:'',opponentFormation:'',
    tacticalFocus:'General review',
    matchObjectives:'',
    intendedRiskProfile:'',
    expectedOpponentBehaviors:'',
    previousIssueMonitored:'',
    ourTeamSide:'team_a',venue:'neutral',_matchId:null
  });

  // ── Key moments ──────────────────────────────────────────────────────────────
  const [keyMoments, setKeyMoments]     = useState([]);
  const [advancedReview, setAdvancedReview] = useState(false);
  const [expandedIdx, setExpandedIdx]   = useState(null);

  // ── Cleanup / conflicts ──────────────────────────────────────────────────────
  const [cleanupNotice, setCleanupNotice]     = useState(null);
  const [proposedCleanup, setProposedCleanup] = useState(null);
  const [conflicts, setConflicts]             = useState([]);

  // ── Import / paste ───────────────────────────────────────────────────────────
  const [showPastePanel, setShowPastePanel] = useState(false);
  const [pasteText, setPasteText]           = useState('');
  const [pastePreview, setPastePreview]     = useState([]);
  const [importPreview, setImportPreview]   = useState(null);
  const [showImportPreview, setShowImportPreview] = useState(false);
  const [importLoading, setImportLoading]   = useState(false);
  const [importError, setImportError]       = useState('');
  const [confirmTeamMismatch, setConfirmTeamMismatch] = useState(false);

  // ── Optional sections ────────────────────────────────────────────────────────
  const [showPlayerReviews, setShowPlayerReviews]       = useState(false);
  const [showDetailedEvidence, setShowDetailedEvidence] = useState(false);
  const [showCoachingObs, setShowCoachingObs]           = useState(false);
  const [roleReviews, setRoleReviews] = useState([]);
  const [footage, setFootage] = useState({
    sourceType:'external',uploadedFile:null,uploadedName:'',
    externalUrl:'',externalPlatform:'',externalNotes:''
  });
  const [notes, setNotes] = useState({ overall:'',concern:'',reviewFirst:'',focusUnit:'' });
  const [videoUpload, setVideoUpload] = useState({}); // { [momentIdx]: { status, progress, error, objectKey } }
  const [playbackUrl, setPlaybackUrl] = useState({}); // { [momentIdx]: url }

  // ── Autosave ─────────────────────────────────────────────────────────────────
  const autosaveRef = useRef(null);
  useEffect(() => {
    const hasDraft = match.opponent || keyMoments.length > 0 || roleReviews.length > 0;
    if (!hasDraft) return;
    clearTimeout(autosaveRef.current);
    autosaveRef.current = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({
          match,footage,notes,keyMoments,roleReviews,
          step,maxStep,lastSaved:new Date().toISOString()
        }));
        setIsDirty(true);
      } catch {}
    }, 1500);
    return () => clearTimeout(autosaveRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match,footage,notes,keyMoments,roleReviews,step,maxStep]);

  // ── Draft restore ─────────────────────────────────────────────────────────────
  useEffect(() => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (!saved) return;
      const d = JSON.parse(saved);
      if (d.match)      setMatch({ ...d.match, teamName:'' });
      if (d.footage)    setFootage(d.footage);
      if (d.notes)      setNotes(d.notes);
      if (d.keyMoments) {
        setKeyMoments(ensureClientIds(d.keyMoments || []));
        const proposed = reconcileMomentImports([], d.keyMoments||[]);
        if (proposed.moments.length > 0 && proposed.moments.length < (d.keyMoments||[]).length) {
          setProposedCleanup({ before:(d.keyMoments||[]).length, after:proposed.moments.length, moments:proposed.moments });
        }
      }
      if (d.roleReviews) setRoleReviews(d.roleReviews);
      if (typeof d.maxStep === 'number') setMaxStep(d.maxStep);
      if (typeof d.step === 'number') setStep(Math.min(d.step, d.maxStep||0, STEPS.length-1));
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load teams ────────────────────────────────────────────────────────────────
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api.getTeams();
        const list = (res && res.teams) ? res.teams : [];
        if (!active) return;
        setTeams(list);
        setMatch(m => {
          const matched = m.teamId ? list.find(t => t.id === m.teamId) : null;
          if (matched) return { ...m, teamName:matched.name };
          if (list.length === 1) return { ...m, teamId:list[0].id, teamName:list[0].name };
          return { ...m, teamId:'', teamName:'' };
        });
      } catch { if (active) setTeams([]); }
      finally  { if (active) setTeamsLoading(false); }
    })();
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Tab close protection ──────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => { if (isDirty && !submitting) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty,submitting]);

  // ── Helpers ───────────────────────────────────────────────────────────────────
  const clearDraft = () => { try { localStorage.removeItem(DRAFT_KEY); setIsDirty(false); } catch {} };
  const selectedTeamName = match.teamName || (teams.find(t => t.id === match.teamId)?.name || '');

  // ── Moment actions ────────────────────────────────────────────────────────────
  // ── Deep clone for undo snapshot (structuredClone with JSON fallback) ────────
  const deepClone = useCallback((val) => {
    if (typeof structuredClone === 'function') return structuredClone(val);
    try { return JSON.parse(JSON.stringify(val)); } catch { return val; }
  }, []);

  // ── Centralized undo invalidation ─────────────────────────────────────────
  // Call on every substantive manual change. Do NOT call for purely visual
  // actions (expanding a row, toggling Simple/Advanced view).
  const invalidateImportUndo = useCallback(() => setImportResult(null), []);

  const addKeyMoment = () => {
    const base = blankMoment ? blankMoment() : { minute:'', what_happened:'', perspective:'', moment_type:'', impact:'', coach_observation:'', phase_of_play:'', video_timestamp:'', evidence_notes:'', team_unit:'', player_or_role:'', tags:[], video_r2_key:'' };
    const m = { ...base, client_input_id: genClientId() };
    const newIdx = keyMoments.length;
    setKeyMoments(prev => [...prev, m]);
    setExpandedIdx(newIdx);
    invalidateImportUndo();
  };

  const updateMoment = useCallback((idx, field, value) => {
    setKeyMoments(prev => prev.map((m, i) => i === idx ? { ...m, [field]:value } : m));
    setImportResult(null);
  }, []);

  const deleteMoment = useCallback((idx) => {
    setKeyMoments(prev => prev.filter((_, i) => i !== idx));
    setExpandedIdx(null);
    invalidateImportUndo();
  }, [invalidateImportUndo]);

  const isBlank = (m) => {
    if (!m) return true;
    return !((m.minute && String(m.minute).trim()) ||
             (m.what_happened && String(m.what_happened).trim()) ||
             (m.perspective && String(m.perspective).trim()) ||
             (m.moment_type && String(m.moment_type).trim()));
  };

  const removeEmptyMoments = () => {
    const cleaned = keyMoments.filter(m => !isBlank(m));
    if (cleaned.length < keyMoments.length) {
      setCleanupNotice(`${keyMoments.length} moments reduced to ${cleaned.length} after removing blanks.`);
      setKeyMoments(cleaned);
      invalidateImportUndo();
    }
  };

  const applySuggestedDefaults = () => {
    setKeyMoments(prev => ensureClientIds(applyAllSmartDefaults(prev.filter(m => !isBlank(m)))));
  };

  // ── Conflict resolution ───────────────────────────────────────────────────────
  const resolveConflict = (choice) => {
    const [head, ...rest] = conflicts;
    if (!head) return;
    setKeyMoments(prev => {
      const key = (m) => `${m.minute}|${(m.moment_type||'').toLowerCase()}`;
      const k = key(head.a);
      if (choice === 'a')    return prev; // a is already in list
      if (choice === 'b')    return prev.map(m => key(m) === k ? head.b : m);
      if (choice === 'both') return [...prev, head.b];
      return prev;
    });
    setConflicts(rest);
    invalidateImportUndo(); // resolving a conflict is a substantive change
  };

  // ── Apply reconciled moments (deep snapshot for full Undo) ────────────────────
  const applyReconciled = useCallback((existing, incoming) => {
    // Deep immutable snapshot structuredClone with JSON fallback
    const snapshot = {
      moments:      deepClone(existing),
      roleReviews:  deepClone(roleReviews),
      match:        deepClone(match),
      footage:      deepClone(footage),
      notes:        deepClone(notes),
      step,
      maxStep,
      cleanupNotice,
    };
    const withDefaults = applyAllSmartDefaults(incoming);
    const reconciled = reconcileMomentImports(existing, withDefaults);
    const beforeCount = existing.filter(m => !isBlank(m)).length;
    const afterCount  = reconciled.moments.length;
    setKeyMoments(ensureClientIds(reconciled.moments));
    const totalIn = beforeCount + incoming.filter(m => !isBlank(m)).length;
    if (totalIn !== afterCount || reconciled.mergedCount > 0 || reconciled.blanksRemoved > 0) {
      setCleanupNotice(`${totalIn} moments → ${afterCount} kept.`);
    }
    if ((reconciled.conflicts || []).length > 0) setConflicts(reconciled.conflicts);
    setImportResult({
      added:    reconciled.newCount    ?? 0,
      merged:   reconciled.mergedCount ?? 0,
      excluded: reconciled.duplicatesRemoved ?? 0,
      conflicts:(reconciled.conflicts || []).length,
      snapshot,
    });
  }, [roleReviews, match, footage, notes, step, maxStep, cleanupNotice, deepClone]);

  // ── Import / paste ───────────────────────────────────────────────────────────
  const handlePasteChange = (val) => {
    setPasteText(val);
    setImportError('');
    if (!val.trim()) { setPastePreview([]); return; }
    const preview = parsePasteText(val, 'Paste');
    setPastePreview(preview.keyMoments||[]);
  };

  const applyPaste = () => {
    const existing = keyMoments.length === 1 && isBlank(keyMoments[0]) ? [] : keyMoments;
    applyReconciled(existing, pastePreview);
    setPasteText(''); setPastePreview([]); setShowPastePanel(false);
  };

  const normalizeTeamCompare = (v) => String(v||'').trim().toLowerCase().replace(/\s+/g,' ');
  const importTeamMismatch = !!(importPreview?.match?.teamNameFromFile && selectedTeamName &&
    normalizeTeamCompare(importPreview.match.teamNameFromFile) !== normalizeTeamCompare(selectedTeamName));

  const handleImportFile = async (file) => {
    setImportError(''); setImportPreview(null); setShowImportPreview(false); setConfirmTeamMismatch(false);
    if (!file) return;
    setImportLoading(true);
    try {
      const preview = await parseMatchImportFile(file);
      setImportPreview(preview); setShowImportPreview(true);
    } catch (err) { setImportError(err.message||'Could not import this file.'); }
    finally { setImportLoading(false); }
  };

  const applyImportPreview = () => {
    if (!importPreview) return;
    if (importTeamMismatch && !confirmTeamMismatch) { setImportError('Confirm the selected team is correct before applying.'); return; }
    const existing = keyMoments.length === 1 && isBlank(keyMoments[0]) ? [] : keyMoments;
    applyReconciled(existing, importPreview.keyMoments||[]);
    setMatch(prev => ({
      ...prev, teamId:prev.teamId, teamName:selectedTeamName||prev.teamName,
      opponent:importPreview.match?.opponent||prev.opponent,
      ourScore:importPreview.match?.ourScore||prev.ourScore,
      opponentScore:importPreview.match?.opponentScore||prev.opponentScore,
      venue:importPreview.match?.venue||prev.venue,
      date:importPreview.match?.date||prev.date,
      competition:importPreview.match?.competition||prev.competition,
      tacticalFocus:importPreview.match?.tacticalFocus||prev.tacticalFocus,
      teamFormation:importPreview.match?.teamFormation||prev.teamFormation,
      opponentFormation:importPreview.match?.opponentFormation||prev.opponentFormation,
    }));
    if ((importPreview.roleReviews||[]).length > 0) {
      setRoleReviews(prev => { const { newReviews } = detectDuplicateRoleReviews(prev, importPreview.roleReviews||[]); return [...prev, ...newReviews]; });
    }
    if (importPreview.evidence) {
      setFootage(prev => ({ ...prev, sourceType:importPreview.evidence.externalUrl?'external':prev.sourceType, externalUrl:importPreview.evidence.externalUrl||prev.externalUrl, externalPlatform:importPreview.evidence.externalPlatform||prev.externalPlatform, externalNotes:importPreview.evidence.externalNotes||prev.externalNotes }));
      setNotes(prev => ({ overall:importPreview.evidence.overall||prev.overall, concern:importPreview.evidence.concern||prev.concern, reviewFirst:importPreview.evidence.reviewFirst||prev.reviewFirst, focusUnit:importPreview.evidence.focusUnit||prev.focusUnit }));
    }
    setStep(1); setMaxStep(m => Math.max(m,1)); setIsDirty(true);
    setShowImportPreview(false); setImportPreview(null); setImportError('');
    setSaveMsg('Import applied'); setTimeout(() => setSaveMsg(''), 2500);
  };

  // ── Match field update (invalidates undo) ───────────────────────────────────
  const updateMatch = useCallback((fields) => {
    setMatch(prev => ({ ...prev, ...fields }));
    invalidateImportUndo();
  }, [invalidateImportUndo]);

  // ── Footage/notes update (invalidates undo) ──────────────────────────────────
  const updateFootage = useCallback((fields) => {
    setFootage(prev => ({ ...prev, ...fields }));
    invalidateImportUndo();
  }, [invalidateImportUndo]);

  // ── Video clip upload to R2 ──────────────────────────────────────────────────
  const resolveMatchId = useCallback(() => match._matchId || match.id || null, [match._matchId, match.id]);

  const ensureMatchSavedForVideo = useCallback(async () => {
    const existing = resolveMatchId();
    if (existing) return existing;
    if (!match.teamId || !match.opponent) {
      throw new Error('Select your team and enter the opponent before uploading video.');
    }
    const liveTeam = teams.find(t => t.id === match.teamId);
    const ourTeamName = liveTeam ? liveTeam.name : (match.teamName || 'Our team');
    const ourScore = match.ourScore === '' ? null : (parseInt(match.ourScore, 10) || 0);
    const opponentScore = match.opponentScore === '' ? null : (parseInt(match.opponentScore, 10) || 0);
    const created = await api.createMatch({
      team_id: match.teamId,
      team_name: ourTeamName,
      opponent: match.opponent,
      our_team_name: ourTeamName,
      opponent_name: match.opponent,
      our_team_side: 'team_a',
      our_score: ourScore,
      opponent_score: opponentScore,
      venue: match.venue || 'neutral',
      match_date: match.date,
      video_source_type: footage.sourceType,
      external_video_url: footage.sourceType === 'external' ? (footage.externalUrl || null) : null,
      original_video_url: footage.sourceType === 'external' ? (footage.externalUrl || null) : null,
      original_submitted_video_url: footage.sourceType === 'external' ? (footage.externalUrl || null) : null,
      external_video_platform: footage.sourceType === 'external' ? (footage.externalPlatform || null) : null,
      external_video_notes: footage.sourceType === 'external' ? (footage.externalNotes || null) : null,
      notes: '',
      team_formation: match.teamFormation || null,
      opponent_formation: match.opponentFormation || null,
      tactical_focus: match.tacticalFocus,
      match_objectives: match.matchObjectives || null,
      intended_risk_profile: match.intendedRiskProfile || null,
      expected_opponent_behaviors: match.expectedOpponentBehaviors || null,
      previous_issue_monitored: match.previousIssueMonitored || null,
      final_score: (ourScore != null && opponentScore != null) ? `${ourScore}-${opponentScore}` : null,
      competition: match.competition || null,
    });
    setMatch(m => ({ ...m, _matchId: created.id }));
    return created.id;
  }, [resolveMatchId, match, teams, footage]);

  const handleVideoUpload = useCallback(async (momentIdx, file) => {
    if (!file) return;
    const allowedTypes = ['video/mp4','video/quicktime','video/x-msvideo','video/webm','video/x-matroska','video/mpeg'];
    if (!allowedTypes.includes(file.type)) { setVideoUpload(prev => ({ ...prev, [momentIdx]: { status:'error', error:'Unsupported file type. MP4, MOV, AVI, WebM, MKV, or MPEG only.' } })); return; }
    if (file.size > 500 * 1024 * 1024) { setVideoUpload(prev => ({ ...prev, [momentIdx]: { status:'error', error:'File exceeds 500 MB limit.' } })); return; }
    setVideoUpload(prev => ({ ...prev, [momentIdx]: { status:'uploading', progress:0, error:null } }));
    try {
      const matchId = await ensureMatchSavedForVideo();
      // Step 1: Get presign (returns S3 URL or binding-mode upload endpoint + objectKey)
      const authHeaders = (() => {
        const token = localStorage.getItem('tactmark_token');
        return {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };
      })();
      const presignRes = await fetch('/api/video-upload/presign', {
        method:'POST', headers: authHeaders,
        body: JSON.stringify({ matchId, contentType: file.type, fileSize: file.size })
      });
      if (!presignRes.ok) { const d = await presignRes.json().catch(()=>({})); throw new Error(d.error||'Failed to get upload URL'); }
      const { uploadUrl, objectKey, mode } = await presignRes.json();
      // Step 2: Upload (S3 direct PUT or Worker binding POST)
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        if (mode === 's3') {
          xhr.open('PUT', uploadUrl, true);
          xhr.setRequestHeader('Content-Type', file.type);
        } else {
          xhr.open('POST', uploadUrl + '?key=' + encodeURIComponent(objectKey) + '&matchId=' + encodeURIComponent(matchId), true);
          xhr.setRequestHeader('Content-Type', file.type);
          const token = localStorage.getItem('tactmark_token');
          if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
        xhr.upload.onprogress = (e) => { if (e.lengthComputable) { const pct = Math.round((e.loaded/e.total)*100); setVideoUpload(prev => ({ ...prev, [momentIdx]: { status:'uploading', progress:pct, error:null } })); } };
        xhr.onload = () => { if (xhr.status >= 200 && xhr.status < 300) resolve(); else reject(new Error('Upload failed ('+xhr.status+')')); };
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.send(file);
      });
      // Step 3: Confirm upload via R2 HEAD
      const confirmRes = await fetch('/api/video-upload/confirm', {
        method:'POST', headers: authHeaders,
        body: JSON.stringify({ objectKey, matchId })
      });
      if (!confirmRes.ok) { const d = await confirmRes.json().catch(()=>({})); throw new Error(d.error||'Upload verification failed'); }
      updateMoment(momentIdx, 'video_r2_key', objectKey);
      setVideoUpload(prev => ({ ...prev, [momentIdx]: { status:'done', progress:100, objectKey, error:null } }));
    } catch (err) {
      setVideoUpload(prev => ({ ...prev, [momentIdx]: { status:'error', error: err.message||'Upload failed. Please retry.' } }));
    }
  }, [ensureMatchSavedForVideo, updateMoment]);

  const handleVideoRemove = useCallback(async (momentIdx) => {
    const km = keyMoments[momentIdx];
    const objectKey = km?.video_r2_key;
    if (objectKey) {
      try {
        const token = localStorage.getItem('tactmark_token');
        await fetch('/api/video-upload', {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ objectKey }),
        });
      } catch {}
    }
    updateMoment(momentIdx, 'video_r2_key', '');
    setVideoUpload(prev => { const next = {...prev}; delete next[momentIdx]; return next; });
  }, [keyMoments, updateMoment]);

  const handleVideoPlayback = useCallback(async (momentIdx) => {
    const km = keyMoments[momentIdx];
    const objectKey = km?.video_r2_key;
    if (!objectKey) return;
    try {
      const token = localStorage.getItem('tactmark_token');
      const res = await fetch('/api/video-upload/signed-url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ objectKey }),
      });
      if (!res.ok) throw new Error('Failed to get playback URL');
      const { signedUrl } = await res.json();
      setPlaybackUrl(prev => ({ ...prev, [momentIdx]: signedUrl }));
    } catch (err) {
      setPlaybackUrl(prev => ({ ...prev, [momentIdx]: null }));
    }
  }, [keyMoments]);

  const updateNotes = useCallback((fields) => {
    setNotes(prev => ({ ...prev, ...fields }));
    invalidateImportUndo();
  }, [invalidateImportUndo]);

  // ── Role review update helpers (invalidate undo) ─────────────────────────────
  const updateRoleReview = useCallback((idx, fields) => {
    setRoleReviews(prev => prev.map((r, i) => i === idx ? { ...r, ...fields } : r));
    invalidateImportUndo();
  }, [invalidateImportUndo]);

  const addRoleReview = useCallback(() => {
    setRoleReviews(prev => [...prev, blankRoleReview()]);
    invalidateImportUndo();
  }, [invalidateImportUndo]);

  const deleteRoleReview = useCallback((idx) => {
    setRoleReviews(prev => prev.filter((_, i) => i !== idx));
    invalidateImportUndo();
  }, [invalidateImportUndo]);

  // ── Navigation ────────────────────────────────────────────────────────────────
  const goToStep = (i) => { if (i > maxStep) return; setError(''); setValidationIssues(null); setStep(i); };
  const back = () => { setError(''); setValidationIssues(null); setStep(s => Math.max(s-1,0)); };
  const next = () => {
    if (step === 0) {
      if (!match.teamId)    { setError('Select your team before continuing.'); return; }
      if (!match.opponent)  { setError('Enter the opponent name.'); return; }
      if (match.opponent.trim().toLowerCase() === selectedTeamName.trim().toLowerCase()) { setError('Your team and opponent cannot be the same.'); return; }
    }
    setError('');
    setStep(s => { const n = Math.min(s+1,STEPS.length-1); setMaxStep(m => Math.max(m,n)); return n; });
  };

  // ── Validate + Generate ───────────────────────────────────────────────────────
  const validateBeforeGenerate = () => {
    const issues = [];
    if (!match.teamId)   issues.push({ msg:'Select your team', step:0, label:'Go to Match Details' });
    if (!match.opponent) issues.push({ msg:'Enter opponent name', step:0, label:'Go to Match Details' });
    if (match.ourScore==='' || match.opponentScore==='') issues.push({ msg:'Enter the final score', step:0, label:'Go to Match Details' });
    const r = computeReadiness(keyMoments);
    if (r.state==='none') issues.push({ msg:'Add at least one key moment', step:1, label:'Add Key Moments' });
    else if (r.state==='needs_review') issues.push({ msg:`${r.incompleteCount} moment${r.incompleteCount!==1?'s':''} missing minute, description, or perspective`, step:1, label:'Fix Key Moments' });
    return issues.length > 0 ? issues : null;
  };

  const handleGenerate = async () => {
    setError('');
    const issues = validateBeforeGenerate();
    if (issues) { setValidationIssues(issues); return; }
    setValidationIssues(null);
    setSubmitting(true);
    try {
      const ourScore = parseInt(match.ourScore,10)||0;
      const opponentScore = parseInt(match.opponentScore,10)||0;
      const liveTeam = teams.find(t => t.id === match.teamId);
      const ourTeamName = liveTeam ? liveTeam.name : match.teamName;

      let created;
      if (match._matchId) {
        created = { id:match._matchId };
      } else {
        created = await api.createMatch({
          team_id:match.teamId, team_name:ourTeamName, opponent:match.opponent,
          our_team_name:ourTeamName, opponent_name:match.opponent, our_team_side:'team_a',
          our_score:ourScore, opponent_score:opponentScore,
          venue:match.venue||'neutral', match_date:match.date,
          video_source_type:footage.sourceType,
          external_video_url:footage.sourceType==='external'?(footage.externalUrl||null):null,
          original_video_url:footage.sourceType==='external'?(footage.externalUrl||null):null,
          original_submitted_video_url:footage.sourceType==='external'?(footage.externalUrl||null):null,
          external_video_platform:footage.sourceType==='external'?(footage.externalPlatform||null):null,
          external_video_notes:footage.sourceType==='external'?(footage.externalNotes||null):null,
          notes:`${notes.overall}${notes.concern?` | Most concerning: ${notes.concern}`:''}${notes.reviewFirst?` | Review first: ${notes.reviewFirst}`:''}${notes.focusUnit?` | Focus unit: ${notes.focusUnit}`:''}`,
          team_formation:match.teamFormation||null, opponent_formation:match.opponentFormation||null,
          tactical_focus:match.tacticalFocus, final_score:`${ourScore}-${opponentScore}`,
          competition:match.competition||null,
          match_objectives:match.matchObjectives||null,
          intended_risk_profile:match.intendedRiskProfile||null,
          expected_opponent_behaviors:match.expectedOpponentBehaviors||null,
          previous_issue_monitored:match.previousIssueMonitored||null
        });
        setMatch(m => ({ ...m, _matchId:created.id }));
      }

      const finalMoments = applyAllSmartDefaults(keyMoments.filter(m => !isBlank(m)));
      for (const km of finalMoments) {
        const momentType = km.moment_type || inferMomentType(km.what_happened||'');
        if (momentType || km.what_happened) {
          const { perspective_source, impact_source, client_input_id: cid, ...apiMoment } = km;
          // Assign a stable client_input_id if the moment somehow lacks one (legacy draft)
          const momentClientId = cid || genClientId();
          await api.createKeyMoment({
            match_id:created.id, minute:apiMoment.minute,
            moment_type:momentType||'Other', what_happened:apiMoment.what_happened,
            coach_observation:apiMoment.coach_observation, impact:apiMoment.impact||'Medium',
            phase_of_play:apiMoment.phase_of_play||null, video_timestamp:apiMoment.video_timestamp||null, video_r2_key:apiMoment.video_r2_key||null,
            evidence_notes:apiMoment.evidence_notes||null, team_unit:apiMoment.team_unit||null,
            player_or_role:apiMoment.player_or_role||null, perspective:apiMoment.perspective||null,
            tags:apiMoment.tags||[], client_input_id:momentClientId
          });
        }
      }

      for (const rr of roleReviews) {
        if (rr.player_name) {
          const { import_source_id, import_source_section, import_source_row, client_input_id: rrCid, ...apiRR } = rr;
          const rrClientId = rrCid || genClientId();
          await api.createRoleReview({ match_id:created.id, ...apiRR, should_review:!!apiRR.should_review, client_input_id:rrClientId });
        }
      }

      // Kick off generation. HTTP 202 means "still generating" NOT done.
      // We must not clear the draft or navigate until a real report exists,
      // otherwise a crashed/slow generation would silently lose the draft.
      const gen = await api.analyzeMatch(created.id);
      const startedButNotDone = gen && (gen.__status === 202 || (gen.status === 'generating' && !gen.reportId && !gen.report_id));

      let reportReady = !!(gen && (gen.reportId || gen.report_id || (gen.report && gen.report.id)));

      if (!reportReady && startedButNotDone) {
        // Poll status until the report exists, generation fails, or we time out.
        // The match + moments are already saved server-side, so nothing is lost.
        const deadline = Date.now() + 90 * 1000;
        while (Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 3000));
          let st;
          try { st = await api.getGenerationStatus(created.id); } catch (_) { st = null; }
          if (st && st.status === 'completed' && st.reportId) { reportReady = true; break; }
          if (st && (st.status === 'failed')) {
            throw new Error('Report generation failed. Your match and key moments are saved use Retry generation.');
          }
        }
      }

      if (reportReady) {
        // Only now is it safe to clear the draft and view the report.
        clearDraft();
        navigate(`/analysis/${created.id}`);
      } else {
        // Timed out still generating. Preserve the draft and send the user to
        // the report page, which will keep polling / offer Retry. Nothing lost.
        setError('Report is taking longer than expected. Your match and key moments are saved opening the match so you can retry generation.');
        navigate(`/analysis/${created.id}`);
      }
    } catch (err) {
      // On any failure the draft is intentionally preserved so the user can retry.
      setError(err.message||'Something went wrong generating this report. Your match and key moments are saved.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Role reviews (addRoleReview, updateRoleReview, deleteRoleReview defined above)
  const removeRoleReview = (i) => deleteRoleReview(i);

  // ── Derived ───────────────────────────────────────────────────────────────────
  const readiness = computeReadiness(keyMoments);

  // ── Step header ───────────────────────────────────────────────────────────────
  const renderStepHeader = () => (
    <div className="flex items-center gap-1 mb-8">
      {STEPS.map((s,i) => {
        const hasIssue    = (validationIssues||[]).some(iss => iss.step===i);
        const isCompleted = i < step && i <= maxStep;
        const isCurrent   = i === step;
        const isClickable = i <= maxStep && i !== step;
        let circleClass = 'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 border-2 ';
        if (hasIssue)         circleClass += 'bg-red-600 text-white border-red-600';
        else if (isCurrent)   circleClass += 'bg-navy-900 text-white border-navy-900 ring-2 ring-navy-200 ring-offset-1';
        else if (isCompleted) circleClass += 'bg-emerald-600 text-white border-emerald-600';
        else                  circleClass += 'bg-gray-100 text-gray-400 border-gray-200';
        let labelClass = 'text-xs hidden sm:inline font-medium truncate max-w-[80px] ';
        if (hasIssue)         labelClass += 'text-red-600';
        else if (isCurrent)   labelClass += 'text-navy-900 font-semibold';
        else if (isCompleted) labelClass += 'text-emerald-700';
        else                  labelClass += 'text-gray-400';
        return (
          <div key={s} className="flex items-center gap-1.5 flex-1 min-w-0">
            <button type="button" disabled={!isClickable} onClick={() => goToStep(i)}
              className={`flex items-center gap-1.5 min-w-0 ${isClickable?'cursor-pointer group':'cursor-default'}`}>
              <div className={circleClass+(isClickable?' group-hover:opacity-80 transition-opacity':'')}>
                {hasIssue?'!':isCompleted?'✓':i+1}
              </div>
              <span className={labelClass+(isClickable?' group-hover:underline':'')}>{s}</span>
            </button>
            {i < STEPS.length-1 && <div className={`flex-1 h-px mx-1 ${i<step?'bg-emerald-500':'bg-gray-200'}`} />}
          </div>
        );
      })}
    </div>
  );

  // ── Simple row ────────────────────────────────────────────────────────────────
  const renderSimpleRow = (km,idx) => {
    const isExpanded = expandedIdx === idx;
    const incomplete = !km.minute || !km.what_happened || !km.perspective;
    return (
      <div key={idx} className={`border-b border-gray-100 last:border-0 ${incomplete?'bg-amber-50':'bg-white'}`}>
        <div className="flex items-center gap-2 px-3 py-2">
          <input className={smallInputCls()+' w-14 text-center font-mono flex-shrink-0'} value={km.minute||''}
            onChange={e => updateMoment(idx,'minute',e.target.value)} placeholder="Min" maxLength={6} />
          <input className={smallInputCls()+' flex-1 min-w-0'} value={km.what_happened||''}
            onChange={e => updateMoment(idx,'what_happened',e.target.value)} placeholder="What happened…" />
          <select className={smallInputCls()+' w-32 flex-shrink-0'} value={km.perspective||''}
            onChange={e => updateMoment(idx,'perspective',e.target.value)}>
            <option value="">Perspective</option>
            {PERSPECTIVE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button type="button" onClick={() => setExpandedIdx(isExpanded?null:idx)}
            className="text-xs font-medium text-navy-700 border border-gray-300 hover:border-navy-400 rounded-md px-2 py-1 flex-shrink-0">
            {isExpanded?'Done':'Edit'}
          </button>
          <button type="button" onClick={() => deleteMoment(idx)} className="text-gray-400 hover:text-red-600 flex-shrink-0">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
        {isExpanded && (
          <div className="px-3 pb-3 pt-1 border-t border-gray-100">
            {renderAdvancedFields(km,idx)}
          </div>
        )}
        {incomplete && !isExpanded && (
          <div className="px-3 pb-2">
            <p className="text-amber-600 text-xs">Missing: {[!km.minute&&'minute',!km.what_happened&&'description',!km.perspective&&'perspective'].filter(Boolean).join(', ')}</p>
          </div>
        )}
      </div>
    );
  };

  // ── Advanced row (full card) ──────────────────────────────────────────────────
  const renderAdvancedRow = (km,idx) => (
    <div key={idx} className="border border-gray-200 rounded-xl p-4 bg-white">
      <div className="flex items-start gap-2 mb-3">
        <input className={smallInputCls()+' w-14 text-center font-mono flex-shrink-0'} value={km.minute||''}
          onChange={e => updateMoment(idx,'minute',e.target.value)} placeholder="Min" maxLength={6} />
        <input className={smallInputCls()+' flex-1'} value={km.what_happened||''}
          onChange={e => updateMoment(idx,'what_happened',e.target.value)} placeholder="What happened…" />
        <button type="button" onClick={() => deleteMoment(idx)} className="text-gray-400 hover:text-red-600 flex-shrink-0 mt-1">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {renderAdvancedFields(km,idx)}
    </div>
  );

  // ── Advanced fields ───────────────────────────────────────────────────────────
  const renderAdvancedFields = (km,idx) => (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-gray-500 text-xs mb-1 block">Moment type</label>
          <select className={inputCls()} value={km.moment_type||''} onChange={e => updateMoment(idx,'moment_type',e.target.value)}>
            <option value="">Infer from description</option>
            {(MOMENT_TYPES||[]).map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label className="text-gray-500 text-xs mb-1 block">Perspective *</label>
          <select className={inputCls()} value={km.perspective||''} onChange={e => updateMoment(idx,'perspective',e.target.value)}>
            <option value="">Select…</option>
            {PERSPECTIVE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-gray-500 text-xs mb-1 block">Impact</label>
          <select className={inputCls()} value={km.impact||''} onChange={e => updateMoment(idx,'impact',e.target.value)}>
            <option value="">Auto (suggested)</option>
            {['Low','Medium','High','Match-changing'].map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label className="text-gray-500 text-xs mb-1 block">Phase of play</label>
          <select className={inputCls()} value={km.phase_of_play||''} onChange={e => updateMoment(idx,'phase_of_play',e.target.value)}>
            <option value=""></option>
            {PHASE_OF_PLAY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label className="text-gray-500 text-xs mb-1 block">Video timestamp</label>
          <input className={inputCls()} value={km.video_timestamp||''} onChange={e => updateMoment(idx,'video_timestamp',e.target.value)} placeholder="e.g. 23:14" />
        </div>
        <div>
          <label className="text-gray-500 text-xs mb-1 block">Player / Role</label>
          <input className={inputCls()} value={km.player_or_role||''} onChange={e => updateMoment(idx,'player_or_role',e.target.value)} placeholder="e.g. Right winger" />
        </div>
        <div>
          <label className="text-gray-500 text-xs mb-1 block">Team unit</label>
          <input className={inputCls()} value={km.team_unit||''} onChange={e => updateMoment(idx,'team_unit',e.target.value)} placeholder="e.g. Defensive line" />
        </div>
        <div>
          <label className="text-gray-500 text-xs mb-1 block">Tags</label>
          <div className="flex flex-wrap gap-1">
            {TAG_OPTIONS.map(tag => (
              <button key={tag} type="button"
                onClick={() => { const tags=km.tags||[]; updateMoment(idx,'tags',tags.includes(tag)?tags.filter(t=>t!==tag):[...tags,tag]); }}
                className={`text-xs px-2 py-1 rounded-full border ${(km.tags||[]).includes(tag)?'bg-navy-900 text-white border-navy-900':'bg-white text-gray-600 border-gray-300'}`}>
                {tag}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-3">
        <label className="text-gray-500 text-xs mb-1 block">Coach observation</label>
        <textarea className={inputCls()+' resize-none'} rows={2} value={km.coach_observation||''} onChange={e => updateMoment(idx,'coach_observation',e.target.value)} placeholder="Tactical note…" />
      </div>
      <div className="mt-3">
        <label className="text-gray-500 text-xs mb-1 block">Evidence notes</label>
        <textarea className={inputCls()+' resize-none'} rows={2} value={km.evidence_notes||''} onChange={e => updateMoment(idx,'evidence_notes',e.target.value)} placeholder="Source reference…" />
      </div>
      <div className="mt-3 border-t border-gray-100 pt-3">
        <label className="text-gray-500 text-xs mb-1 block flex items-center gap-1"><Film className="w-3 h-3" /> Video clip (R2 upload, max 500 MB)</label>
        {(() => {
          const vu = videoUpload[idx] || {};
          if (km.video_r2_key && vu.status !== 'uploading') {
            return (
              <div className="space-y-2">
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-2">
                  <Film className="w-3 h-3 text-green-600" />
                  <span className="text-xs text-green-700 flex-1 truncate">Clip attached: {km.video_r2_key.split('/').pop()}</span>
                  <button type="button" onClick={()=>handleVideoPlayback(idx)} className="text-xs text-navy-900 hover:underline flex items-center gap-1"><Play className="w-3 h-3" />Play</button>
                  <label className="text-xs text-navy-900 hover:underline flex items-center gap-1 cursor-pointer">
                    <RefreshCw className="w-3 h-3" />Replace
                    <input type="file" accept="video/mp4,video/quicktime,video/x-msvideo,video/webm,video/x-matroska,video/mpeg" className="hidden" onChange={e=>{const f=e.target.files[0];if(f){handleVideoRemove(idx).then(()=>handleVideoUpload(idx,f));}e.target.value='';}} />
                  </label>
                  <button type="button" onClick={()=>handleVideoRemove(idx)} className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1"><X className="w-3 h-3" />Remove</button>
                </div>
                {playbackUrl[idx] && (
                  <video src={playbackUrl[idx]} controls className="w-full rounded-lg border border-gray-200 max-h-48" />
                )}
              </div>
            );
          }
          if (vu.status === 'uploading') {
            return (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                <div className="flex items-center gap-2 mb-1"><Loader2 className="w-3 h-3 animate-spin text-blue-600" /><span className="text-xs text-blue-700">Uploading... {vu.progress||0}%</span></div>
                <div className="w-full bg-blue-100 rounded-full h-1.5"><div className="bg-blue-600 h-1.5 rounded-full transition-all" style={{width:`${vu.progress||0}%`}} /></div>
              </div>
            );
          }
          if (vu.status === 'error') {
            return (
              <div className="space-y-2">
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-2">
                  <AlertCircle className="w-3 h-3 text-red-600" /><span className="text-xs text-red-700 flex-1">{vu.error}</span>
                </div>
                <label className="flex items-center gap-2 cursor-pointer text-xs text-navy-900 hover:underline">
                  <Upload className="w-3 h-3" />Retry upload
                  <input type="file" accept="video/mp4,video/quicktime,video/x-msvideo,video/webm,video/x-matroska,video/mpeg" className="hidden" onChange={e=>{const f=e.target.files[0];if(f)handleVideoUpload(idx,f);e.target.value='';}} />
                </label>
              </div>
            );
          }
          return (
            <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-500 hover:text-navy-900 border border-dashed border-gray-300 rounded-lg p-2">
              <Upload className="w-3 h-3" />Upload video clip (MP4, MOV, etc.)
              <input type="file" accept="video/mp4,video/quicktime,video/x-msvideo,video/webm,video/x-matroska,video/mpeg" className="hidden" onChange={e=>{const f=e.target.files[0];if(f)handleVideoUpload(idx,f);e.target.value='';}} />
            </label>
          );
        })()}
      </div>
    </div>
  );

  // ── Readiness bar ─────────────────────────────────────────────────────────────
  const renderReadinessBar = () => {
    const missingPerspective = keyMoments.filter(m => !isBlank(m) && !m.perspective).length;
    const hasEmpty = keyMoments.some(m => isBlank(m));
    return (
      <div className="flex flex-wrap items-center gap-3 mb-5 p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm">
        <span className="font-semibold text-navy-900">{readiness.count} moment{readiness.count!==1?'s':''}</span>
        <span className={`ml-auto font-semibold px-3 py-1 rounded-full border text-xs ${
          readiness.state==='none'?'bg-gray-100 text-gray-500 border-gray-200':
          readiness.state==='ready'?'bg-emerald-50 text-emerald-700 border-emerald-200':
          'bg-amber-50 text-amber-700 border-amber-200'}`}>
          {readiness.state==='none'?'No moments added':
           readiness.state==='ready'?'Ready to generate':
           `Needs review ${readiness.incompleteCount} moment${readiness.incompleteCount!==1?'s':''} need attention`}
        </span>
        {cleanupNotice && (
          <div className="basis-full flex items-center justify-between gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
            <span>{cleanupNotice}</span>
            <button type="button" onClick={() => setCleanupNotice(null)} className="font-bold">×</button>
          </div>
        )}
        {proposedCleanup && !cleanupNotice && (
          <div className="basis-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-blue-900 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            <span>{proposedCleanup.before} moments found. {proposedCleanup.after} unique moments will be kept.</span>
            <div className="flex gap-2">
              <button type="button" onClick={() => { setKeyMoments(proposedCleanup.moments); setCleanupNotice(`${proposedCleanup.before} moments found. ${proposedCleanup.after} unique moments will be kept.`); setProposedCleanup(null); }}
                className="bg-navy-900 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">Accept cleanup</button>
              <button type="button" onClick={() => { setAdvancedReview(true); setProposedCleanup(null); }}
                className="bg-white border border-blue-300 text-blue-800 text-xs font-semibold px-3 py-1.5 rounded-lg">Review changes</button>
            </div>
          </div>
        )}
        {readiness.state==='needs_review' && (
          <div className="basis-full flex flex-wrap gap-2 pt-1">
            {missingPerspective > 0 && (
              <button type="button" onClick={applySuggestedDefaults}
                className="bg-white border border-amber-300 text-amber-800 text-xs font-semibold px-3 py-1.5 rounded-lg">
                Apply {missingPerspective} suggested perspective{missingPerspective!==1?'s':''}
              </button>
            )}
            {hasEmpty && (
              <button type="button" onClick={removeEmptyMoments}
                className="bg-white border border-gray-300 text-gray-700 text-xs font-semibold px-3 py-1.5 rounded-lg">
                Remove empty moments
              </button>
            )}
            {readiness.incompleteCount > 0 && (
              <button type="button" onClick={() => setExpandedIdx(readiness.incompleteIndexes[0]??0)}
                className="bg-navy-900 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">
                Review {readiness.incompleteCount} uncertain moment{readiness.incompleteCount!==1?'s':''}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── Conflict prompt ───────────────────────────────────────────────────────────
  const renderConflictPrompt = () => {
    if (conflicts.length===0) return null;
    const c = conflicts[0];
    return (
      <div className="mb-5 bg-amber-50 border border-amber-200 rounded-xl p-4">
        <p className="text-sm font-semibold text-amber-800 mb-1">Conflicting moments at {c.minute}'</p>
        <p className="text-xs text-amber-700 mb-3">Two versions of the {c.minute}' {c.type||'moment'} were found. Choose which to keep.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3 text-xs">
          <div className="bg-white border border-amber-200 rounded-lg p-2">
            <p className="font-semibold text-gray-700 mb-1">Version A</p>
            <p className="text-gray-600">{c.a.what_happened||'(no description)'}</p>
            <p className="text-gray-400 mt-1">{perspectiveLabel?perspectiveLabel(c.a.perspective):c.a.perspective} · {c.a.impact||'no impact'}</p>
          </div>
          <div className="bg-white border border-amber-200 rounded-lg p-2">
            <p className="font-semibold text-gray-700 mb-1">Version B</p>
            <p className="text-gray-600">{c.b.what_happened||'(no description)'}</p>
            <p className="text-gray-400 mt-1">{perspectiveLabel?perspectiveLabel(c.b.perspective):c.b.perspective} · {c.b.impact||'no impact'}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button type="button" onClick={() => resolveConflict('a')} className="text-xs font-semibold border border-gray-300 text-gray-700 rounded-lg px-3 py-1.5 hover:border-navy-400">Keep A</button>
          <button type="button" onClick={() => resolveConflict('b')} className="text-xs font-semibold border border-gray-300 text-gray-700 rounded-lg px-3 py-1.5 hover:border-navy-400">Keep B</button>
          <button type="button" onClick={() => resolveConflict('both')} className="text-xs font-semibold bg-navy-900 text-white rounded-lg px-3 py-1.5">Keep both</button>
          <button type="button" onClick={() => setConflicts(prev => prev.slice(1))} className="text-xs font-medium text-gray-400 hover:text-gray-600 ml-auto">Skip</button>
        </div>
        {conflicts.length>1 && <p className="text-xs text-amber-600 mt-2">{conflicts.length-1} more conflict{conflicts.length-1!==1?'s':''} to resolve.</p>}
      </div>
    );
  };

  // ── Optional toggle ───────────────────────────────────────────────────────────
  const OptionalToggle = ({ open, onToggle, label }) => (
    <button type="button" onClick={onToggle}
      className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-gray-200 bg-white hover:border-navy-400 transition-colors text-sm font-medium text-navy-900">
      <span>{label}</span>
      {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
    </button>
  );

  // ── Import preview ────────────────────────────────────────────────────────────
  const renderImportPreview = () => {
    if (!showImportPreview || !importPreview) return null;
    const existing = keyMoments.length===1 && isBlank(keyMoments[0]) ? [] : keyMoments;
    const duplicateInfo = detectDuplicateMoments(existing, importPreview.keyMoments||[]);
    return (
      <div className="mb-5 bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
          <div>
            <p className="text-sm font-bold text-navy-900">Import preview: {importPreview.source?.fileName}</p>
            <p className="text-xs text-gray-500 mt-1">Parsed locally. No records written until you apply.</p>
          </div>
          <button type="button" onClick={() => { setShowImportPreview(false); setImportPreview(null); setImportError(''); }}
            className="text-xs text-gray-500 hover:text-red-600 font-medium">Cancel</button>
        </div>
        <div className="mb-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-800">
          XLSX template imports the full analysis workflow. CSV, TXT, and DOCX import key moments only.
        </div>
        <div className="grid grid-cols-3 gap-2 mb-4 text-xs">
          <div className="bg-gray-50 rounded-lg px-3 py-2"><p className="uppercase text-gray-400 font-bold text-[10px]">Moments</p><p className="text-lg font-bold text-navy-900">{(importPreview.keyMoments||[]).length}</p></div>
          <div className="bg-gray-50 rounded-lg px-3 py-2"><p className="uppercase text-gray-400 font-bold text-[10px]">Auto-cleaned</p><p className="text-lg font-bold text-gray-700">{duplicateInfo.duplicates.length}</p></div>
          <div className="bg-gray-50 rounded-lg px-3 py-2"><p className="uppercase text-gray-400 font-bold text-[10px]">Skipped</p><p className="text-lg font-bold text-red-700">{(importPreview.skippedRows||[]).length}</p></div>
        </div>
        {importTeamMismatch && (
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm font-semibold text-amber-800">Team name mismatch</p>
            <p className="text-xs text-amber-700 mt-1">File says "<strong>{importPreview.match.teamNameFromFile}</strong>" but selected team is <strong>{selectedTeamName}</strong>.</p>
            <label className="mt-2 flex items-center gap-2 text-xs text-amber-800 font-medium">
              <input type="checkbox" checked={confirmTeamMismatch} onChange={e => setConfirmTeamMismatch(e.target.checked)} />
              Confirm selected team is correct
            </label>
          </div>
        )}
        <div className="flex gap-3">
          <button type="button" onClick={applyImportPreview} disabled={importTeamMismatch && !confirmTeamMismatch}
            className="bg-navy-900 text-white text-sm font-semibold px-4 py-2.5 rounded-lg hover:bg-navy-800 disabled:opacity-50">
            Apply import
          </button>
          <button type="button" onClick={() => { setShowImportPreview(false); setImportPreview(null); setImportError(''); }}
            className="border border-gray-300 text-gray-600 text-sm px-4 py-2.5 rounded-lg">Cancel</button>
        </div>
      </div>
    );
  };

  // ──────────────────────────────────────────────────────────────────────────────
  // MAIN RENDER
  // ──────────────────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-navy-900 mb-1">New Analysis</h1>
      <p className="text-gray-500 mb-6">Add key moments and generate the coaching report.</p>

      {renderStepHeader()}

      {step > 0 && (
        <div className="mb-4">
          <button type="button" onClick={back}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-navy-700 hover:text-navy-900 border border-gray-200 hover:border-navy-400 bg-white rounded-lg px-4 py-2 transition-colors shadow-sm">
            <ChevronLeft className="w-4 h-4" /> Back to {STEPS[step-1]}
          </button>
        </div>
      )}

      {validationIssues && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
            <p className="text-red-700 text-sm font-semibold">Fix the following before generating:</p>
          </div>
          <ul className="space-y-2">
            {validationIssues.map((issue,idx) => (
              <li key={idx} className="flex items-center justify-between gap-3 bg-white border border-red-100 rounded-lg px-3 py-2">
                <span className="text-red-700 text-sm">{issue.msg}</span>
                <button onClick={() => { setValidationIssues(null); setStep(issue.step); }}
                  className="flex-shrink-0 text-xs font-medium bg-red-600 text-white rounded-md px-3 py-1 hover:bg-red-700">{issue.label} →</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && !validationIssues && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-4">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}
      {saveMsg && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm rounded-lg px-4 py-2 mb-4">{saveMsg}</div>
      )}

      {/* ── STEP 0: MATCH DETAILS ─────────────────────────────────────────────── */}
      {step === 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-gray-500 text-sm mb-1 block">Your Team *</label>
              {teamsLoading ? (
                <div className={inputCls()+' flex items-center text-gray-400'}><Loader2 className="w-4 h-4 animate-spin mr-2" />Loading teams…</div>
              ) : teams.length===0 ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  No teams yet. <button type="button" className="underline font-medium" onClick={() => navigate('/teams')}>Create a team</button> first.
                </div>
              ) : (
                <select className={inputCls()} value={match.teamId}
                  onChange={e => { const t=teams.find(x => x.id===e.target.value); updateMatch({teamId:e.target.value,teamName:t?t.name:''}); }}>
                  <option value="">Select your team</option>
                  {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              )}
            </div>
            <div>
              <label className="text-gray-500 text-sm mb-1 block">Opponent *</label>
              <input className={inputCls()} value={match.opponent} onChange={e => updateMatch({opponent:e.target.value})} placeholder="e.g. City Rivals FC" />
              {match.opponent && selectedTeamName && match.opponent.trim().toLowerCase()===selectedTeamName.trim().toLowerCase() && (
                <p className="text-red-500 text-xs mt-1">Your team and opponent cannot be the same.</p>
              )}
            </div>
          </div>

          <div>
            <label className="text-gray-700 text-sm font-semibold mb-2 block">Final Score *</label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-gray-400 text-xs mb-1 block">{selectedTeamName||'Our team'} (Our Score)</label>
                <input type="number" min="0" max="99" className={inputCls()} value={match.ourScore} onChange={e => updateMatch({ourScore:e.target.value})} placeholder="0" />
              </div>
              <div>
                <label className="text-gray-400 text-xs mb-1 block">{match.opponent||'Opponent'} (Score)</label>
                <input type="number" min="0" max="99" className={inputCls()} value={match.opponentScore} onChange={e => updateMatch({opponentScore:e.target.value})} placeholder="0" />
              </div>
            </div>
          </div>

          <div>
            <label className="text-gray-700 text-sm font-semibold mb-2 block">Venue</label>
            <div className="flex gap-2">
              {['home','away','neutral'].map(v => (
                <button key={v} type="button" onClick={() => updateMatch({venue:v})}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors capitalize ${match.venue===v?'bg-navy-900 text-white border-navy-900':'bg-white text-gray-600 border-gray-300 hover:border-navy-400'}`}>
                  {v}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className="text-gray-500 text-sm mb-1 block">Match Date</label>
              <input type="date" className={inputCls()} value={match.date} onChange={e => updateMatch({date:e.target.value})} /></div>
            <div><label className="text-gray-500 text-sm mb-1 block">Competition</label>
              <input className={inputCls()} value={match.competition} onChange={e => updateMatch({competition:e.target.value})} placeholder="e.g. League match" /></div>
            <div><label className="text-gray-500 text-sm mb-1 block">Tactical Focus</label>
              <select className={inputCls()} value={match.tacticalFocus} onChange={e => updateMatch({tacticalFocus:e.target.value})}>
                {TACTICAL_FOCUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select></div>
            <div><label className="text-gray-500 text-sm mb-1 block">Your Formation</label>
              <input className={inputCls()} value={match.teamFormation} onChange={e => updateMatch({teamFormation:e.target.value})} placeholder="e.g. 4-3-3" /></div>
            <div><label className="text-gray-500 text-sm mb-1 block">Opponent Formation</label>
              <input className={inputCls()} value={match.opponentFormation} onChange={e => updateMatch({opponentFormation:e.target.value})} placeholder="e.g. 4-4-2" /></div>
          </div>

          <div className="mt-4 border border-navy-100 rounded-xl bg-slate-50 p-4 space-y-3">
            <p className="text-xs font-semibold text-navy-700 uppercase tracking-wide">Pre-match staff context</p>
            <p className="text-xs text-gray-500">Connect this match to what staff intended — so Post-Match evaluates execution, not only outcomes.</p>
            <div>
              <label className="text-gray-500 text-sm mb-1 block">Match objectives</label>
              <textarea className={inputCls()} rows={2} value={match.matchObjectives}
                onChange={e => updateMatch({matchObjectives:e.target.value})}
                placeholder="e.g. Control central progression; protect rest defense when level late" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-gray-500 text-sm mb-1 block">Intended risk profile</label>
                <input className={inputCls()} value={match.intendedRiskProfile}
                  onChange={e => updateMatch({intendedRiskProfile:e.target.value})}
                  placeholder="e.g. Controlled when level after 70" />
              </div>
              <div>
                <label className="text-gray-500 text-sm mb-1 block">Previous issue monitored</label>
                <input className={inputCls()} value={match.previousIssueMonitored}
                  onChange={e => updateMatch({previousIssueMonitored:e.target.value})}
                  placeholder="e.g. Rest-defense after turnovers" />
              </div>
            </div>
            <div>
              <label className="text-gray-500 text-sm mb-1 block">Expected opponent behaviors</label>
              <textarea className={inputCls()} rows={2} value={match.expectedOpponentBehaviors}
                onChange={e => updateMatch({expectedOpponentBehaviors:e.target.value})}
                placeholder="e.g. High press on center-backs; quick counters into wide channels" />
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button type="button" onClick={next}
              className="bg-navy-900 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-navy-800 transition-colors">
              Next: Key Moments →
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 1: KEY MOMENTS ──────────────────────────────────────────────── */}
      {step === 1 && (
        <div>
          {renderReadinessBar()}
          {importResult && (
            <div className="mb-4 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex flex-wrap items-center justify-between gap-3 text-sm">
              <span className="text-blue-900 font-medium">
                {importResult.added > 0 && <span>{importResult.added} added · </span>}
                {importResult.merged > 0 && <span>{importResult.merged} merged · </span>}
                {importResult.excluded > 0 && <span>{importResult.excluded} excluded · </span>}
                {importResult.conflicts > 0 && <span>{importResult.conflicts} need review · </span>}
              </span>
              <button
                type="button"
                onClick={() => {
                  const snap = importResult.snapshot;
                  if (!snap) { setImportResult(null); return; }
                  setKeyMoments(snap.moments ?? []);
                  setRoleReviews(snap.roleReviews ?? []);
                  setMatch(prev => snap.match ? { ...prev, ...snap.match } : prev);
                  setFootage(prev => snap.footage ? { ...prev, ...snap.footage } : prev);
                  setNotes(prev => snap.notes ? { ...prev, ...snap.notes } : prev);
                  if (snap.step     !== undefined) setStep(snap.step);
                  if (snap.maxStep  !== undefined) setMaxStep(snap.maxStep);
                  setCleanupNotice(snap.cleanupNotice ?? null);
                  setConflicts([]);
                  setImportResult(null);
                }}
                className="text-xs font-semibold text-blue-700 border border-blue-300 bg-white rounded-lg px-3 py-1.5 hover:border-blue-500"
              >
                Undo import
              </button>
            </div>
          )}
          {renderConflictPrompt()}

          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-gray-500">{advancedReview?'Advanced Review all fields':'Simple Review compact view'}</p>
            <button type="button" onClick={() => { setAdvancedReview(v => !v); setExpandedIdx(null); }}
              className="text-xs font-semibold border border-gray-300 text-navy-700 hover:border-navy-500 rounded-lg px-3 py-1.5">
              {advancedReview?'Simple Review':'Advanced Review'}
            </button>
          </div>

          {keyMoments.length > 0 && (
            <div className={`mb-4 border border-gray-200 rounded-xl overflow-hidden ${advancedReview?'p-2 space-y-2':''}`}>
              {!advancedReview && (
                <div className="hidden sm:grid bg-gray-50 border-b border-gray-100 px-3 py-1.5 text-[10px] uppercase font-semibold text-gray-400"
                  style={{ gridTemplateColumns:'3.5rem 1fr 8rem 4rem 2rem' }}>
                  <span>Min</span><span>Description</span><span>Perspective</span><span></span><span></span>
                </div>
              )}
              {keyMoments.map((km,idx) => advancedReview ? renderAdvancedRow(km,idx) : renderSimpleRow(km,idx))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 mb-6">
            <button type="button" onClick={addKeyMoment}
              className="inline-flex items-center gap-2 bg-navy-900 hover:bg-navy-800 text-white text-sm font-medium px-4 py-2.5 rounded-lg transition-colors">
              <Plus className="w-4 h-4" /> Add key moment
            </button>
            <label className="inline-flex items-center gap-2 bg-white border border-navy-300 text-navy-900 hover:border-navy-600 text-sm font-semibold px-4 py-2.5 rounded-lg cursor-pointer">
              Import match file
              <input type="file" accept=".xlsx,.csv,.txt,.docx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className="hidden" onChange={e => handleImportFile(e.target.files?.[0])} />
            </label>
            <button type="button" onClick={downloadTemplate}
              className="inline-flex items-center gap-2 border border-gray-300 text-gray-600 hover:border-navy-400 hover:text-navy-900 text-sm font-medium px-4 py-2.5 rounded-lg">
              Download XLSX template
            </button>
            <button type="button" onClick={() => setShowPastePanel(p => !p)}
              className="inline-flex items-center gap-2 border border-gray-300 text-gray-600 hover:border-navy-400 hover:text-navy-900 text-sm font-medium px-4 py-2.5 rounded-lg">
              <Copy className="w-4 h-4" /> {showPastePanel?'Hide paste':'Paste moments'}
            </button>
          </div>

          {importLoading && (
            <div className="mb-5 bg-blue-50 border border-blue-200 text-blue-700 rounded-xl px-4 py-3 text-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Parsing import file…
            </div>
          )}
          {importError && <div className="mb-5 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">{importError}</div>}
          {renderImportPreview()}

          {showPastePanel && (
            <div className="mb-5 bg-white border border-gray-200 rounded-xl p-4">
              <p className="text-sm font-bold text-navy-900 mb-1">Paste moments</p>
              <p className="text-xs text-gray-500 mb-3">Recommended: Minute | Moment Type | Perspective | Impact | What Happened | Coach Observation | Phase of Play. Simpler 4-column format also accepted: Minute | Moment Type | Perspective | What Happened</p>
              <textarea className={inputCls()+' resize-none mb-3'} rows={6} value={pasteText} onChange={e => handlePasteChange(e.target.value)}
                placeholder={"11 | France opened the scoring | For us\n18 | Spain equalized | Against us"} />
              {pastePreview.length > 0 && (
                <div className="mb-3 bg-gray-50 border border-gray-200 rounded-lg p-2">
                  <p className="text-xs text-gray-500 mb-1">{pastePreview.length} moment{pastePreview.length!==1?'s':''} detected:</p>
                  {pastePreview.slice(0,3).map((m,i) => (
                    <p key={i} className="text-xs text-gray-700 font-mono">{m.minute}' | {m.what_happened} | {perspectiveLabel?perspectiveLabel(m.perspective):(m.perspective||'')}</p>
                  ))}
                  {pastePreview.length > 3 && <p className="text-xs text-gray-400">…and {pastePreview.length-3} more</p>}
                </div>
              )}
              <div className="flex gap-3">
                <button type="button" onClick={applyPaste} disabled={pastePreview.length===0}
                  className="bg-navy-900 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50">
                  Apply {pastePreview.length>0?`${pastePreview.length} moments`:''}
                </button>
                <button type="button" onClick={() => { setShowPastePanel(false); setPasteText(''); setPastePreview([]); }}
                  className="border border-gray-300 text-gray-600 text-sm px-4 py-2 rounded-lg">Cancel</button>
              </div>
            </div>
          )}

          {/* Optional sections */}
          <div className="space-y-3 mb-6">
            <OptionalToggle open={showPlayerReviews} onToggle={() => setShowPlayerReviews(v => !v)}
              label={`Add player reviews${roleReviews.length>0?` (${roleReviews.length})`:''}`} />
            {showPlayerReviews && (
              <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
                <p className="text-gray-400 text-xs">Review whether a player's role and minutes matched the game state.</p>
                {roleReviews.map((rr,i) => (
                  <div key={i} className="border border-gray-200 rounded-xl p-4 relative">
                    <button onClick={() => removeRoleReview(i)} className="absolute top-3 right-3 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                      <div><label className="text-gray-500 text-xs mb-1 block">Player Name</label><input className={inputCls()} value={rr.player_name} onChange={e => updateRoleReview(i,'player_name',e.target.value)} placeholder="e.g. J. Alvarez" /></div>
                      <div><label className="text-gray-500 text-xs mb-1 block">Position / Role</label><input className={inputCls()} value={rr.position_or_role} onChange={e => updateRoleReview(i,'position_or_role',e.target.value)} placeholder="e.g. Striker" /></div>
                      <div><label className="text-gray-500 text-xs mb-1 block">Minutes Played</label><input type="number" className={inputCls()} value={rr.minutes_played} onChange={e => updateRoleReview(i,'minutes_played',e.target.value)} placeholder="e.g. 75" /></div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                      <div><label className="text-gray-500 text-xs mb-1 block">Positive contribution</label><textarea className={inputCls()+' resize-none'} rows={2} value={rr.positive_contribution} onChange={e => updateRoleReview(i,'positive_contribution',e.target.value)} placeholder="What did this player do well?" /></div>
                      <div><label className="text-gray-500 text-xs mb-1 block">Main concern</label><textarea className={inputCls()+' resize-none'} rows={2} value={rr.main_concern} onChange={e => updateRoleReview(i,'main_concern',e.target.value)} placeholder="Primary worry" /></div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                      <div><label className="text-gray-500 text-xs mb-1 block">Repeated issue</label><input className={inputCls()} value={rr.repeated_issue} onChange={e => updateRoleReview(i,'repeated_issue',e.target.value)} placeholder="e.g. Loses marker at set pieces" /></div>
                      <div><label className="text-gray-500 text-xs mb-1 block">Training focus</label><input className={inputCls()} value={rr.training_focus} onChange={e => updateRoleReview(i,'training_focus',e.target.value)} placeholder="e.g. Positioning in defensive block" /></div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                      <div><label className="text-gray-500 text-xs mb-1 block">Pressing</label><select className={inputCls()} value={rr.pressing_contribution} onChange={e => updateRoleReview(i,'pressing_contribution',e.target.value)}>{CONTRIBUTION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}</select></div>
                      <div><label className="text-gray-500 text-xs mb-1 block">Attacking</label><select className={inputCls()} value={rr.attacking_contribution} onChange={e => updateRoleReview(i,'attacking_contribution',e.target.value)}>{CONTRIBUTION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}</select></div>
                      <div><label className="text-gray-500 text-xs mb-1 block">Defensive</label><select className={inputCls()} value={rr.defensive_contribution} onChange={e => updateRoleReview(i,'defensive_contribution',e.target.value)}>{CONTRIBUTION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}</select></div>
                    </div>
                    <div className="mb-3"><label className="text-gray-500 text-xs mb-1 block">Decision question</label>
                      <textarea className={inputCls()+' resize-none'} rows={2} value={rr.decision_question} onChange={e => updateRoleReview(i,'decision_question',e.target.value)} placeholder="e.g. Should we switch to a back three?" /></div>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" id={`review_${i}`} checked={rr.should_review} onChange={e => updateRoleReview(i,'should_review',e.target.checked)} />
                      <label htmlFor={`review_${i}`} className="text-sm text-gray-700">Flag for staff review</label>
                    </div>
                  </div>
                ))}
                <button onClick={addRoleReview} className="flex items-center gap-2 text-navy-600 text-sm font-medium hover:text-navy-900">
                  <Plus className="w-4 h-4" /> Add player / role review
                </button>
              </div>
            )}

            <OptionalToggle open={showDetailedEvidence} onToggle={() => setShowDetailedEvidence(v => !v)} label="Add match evidence" />
            {showDetailedEvidence && (
              <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
                <div><label className="text-gray-500 text-sm mb-1 block">Video / footage URL</label>
                  <input className={inputCls()} value={footage.externalUrl} onChange={e => updateFootage({externalUrl:e.target.value})} placeholder="https://…" /></div>
                <div><label className="text-gray-500 text-sm mb-1 block">Evidence notes</label>
                  <input className={inputCls()} value={footage.externalNotes} onChange={e => updateFootage({externalNotes:e.target.value})} placeholder="Notes about the footage source" /></div>
              </div>
            )}

            <OptionalToggle open={showCoachingObs} onToggle={() => setShowCoachingObs(v => !v)} label="Add detailed coaching observations" />
            {showCoachingObs && (
              <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
                <div><label className="text-gray-500 text-sm mb-1 block">Overall match notes</label>
                  <textarea className={inputCls()+' resize-none'} rows={3} value={notes.overall} onChange={e => updateNotes({overall:e.target.value})} placeholder="Key moments, tactical decisions…" /></div>
                <div><label className="text-gray-500 text-sm mb-1 block">What concerned you most?</label>
                  <input className={inputCls()} value={notes.concern} onChange={e => updateNotes({concern:e.target.value})} /></div>
                <div><label className="text-gray-500 text-sm mb-1 block">What should staff review first?</label>
                  <input className={inputCls()} value={notes.reviewFirst} onChange={e => updateNotes({reviewFirst:e.target.value})} /></div>
                <div><label className="text-gray-500 text-sm mb-1 block">Focus unit for training</label>
                  <input className={inputCls()} value={notes.focusUnit} onChange={e => updateNotes({focusUnit:e.target.value})} /></div>
              </div>
            )}
          </div>

          {/* GENERATE BUTTON */}
          <div className="bg-white border-2 border-navy-200 rounded-2xl p-6 text-center">
            <p className="text-gray-600 text-sm mb-4">
              {readiness.state==='ready'?`${readiness.count} moments are ready. Generate the coaching report now.`:
               readiness.state==='none'?'Add key moments above, then generate the report.':
               `${readiness.incompleteCount} moment${readiness.incompleteCount!==1?'s':''} still need attention. Fix them above or generate now.`}
            </p>
            <button type="button" onClick={handleGenerate} disabled={submitting}
              className="inline-flex items-center justify-center gap-2 bg-navy-900 hover:bg-navy-800 text-white font-semibold px-8 py-3 rounded-xl text-base transition-colors disabled:opacity-60 w-full sm:w-auto">
              {submitting?<><Loader2 className="w-4 h-4 animate-spin" />Generating…</>:'Generate coaching report'}
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 2: GENERATE REPORT ──────────────────────────────────────────── */}
      {step === 2 && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6">
          <h2 className="text-xl font-bold text-navy-900 mb-4">Ready to generate</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <div className="bg-gray-50 rounded-xl p-3 text-center"><p className="text-[10px] uppercase text-gray-400 font-bold">Your team</p><p className="text-sm font-semibold text-navy-900 mt-1 truncate">{selectedTeamName||''}</p></div>
            <div className="bg-gray-50 rounded-xl p-3 text-center"><p className="text-[10px] uppercase text-gray-400 font-bold">Opponent</p><p className="text-sm font-semibold text-navy-900 mt-1 truncate">{match.opponent||''}</p></div>
            <div className="bg-gray-50 rounded-xl p-3 text-center"><p className="text-[10px] uppercase text-gray-400 font-bold">Score</p><p className="text-sm font-semibold text-navy-900 mt-1">{match.ourScore!==''&&match.opponentScore!==''?`${match.ourScore}-${match.opponentScore}`:''}</p></div>
            <div className="bg-gray-50 rounded-xl p-3 text-center"><p className="text-[10px] uppercase text-gray-400 font-bold">Moments</p><p className="text-sm font-semibold text-navy-900 mt-1">{readiness.count}</p></div>
          </div>
          {readiness.state==='needs_review' && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <p className="text-amber-800 text-sm font-semibold">{readiness.incompleteCount} moment{readiness.incompleteCount!==1?'s':''} need attention</p>
              <p className="text-amber-700 text-xs mt-1">You can generate now missing types and impacts will be inferred automatically.</p>
              <button type="button" onClick={() => goToStep(1)} className="mt-3 text-xs font-semibold border border-amber-300 text-amber-800 rounded-lg px-3 py-1.5">Review moments →</button>
            </div>
          )}
          {readiness.state==='none' && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <p className="text-red-700 text-sm font-semibold">No key moments added yet</p>
              <button type="button" onClick={() => goToStep(1)} className="mt-3 text-xs font-semibold bg-red-600 text-white rounded-lg px-3 py-1.5">Add key moments →</button>
            </div>
          )}
          <button type="button" onClick={handleGenerate} disabled={submitting||readiness.state==='none'}
            className="w-full inline-flex items-center justify-center gap-2 bg-navy-900 hover:bg-navy-800 text-white font-semibold px-8 py-4 rounded-xl text-lg disabled:opacity-60">
            {submitting?<><Loader2 className="w-5 h-5 animate-spin" />Generating report…</>:'Generate coaching report'}
          </button>
          {submitting && <p className="text-center text-xs text-gray-400 mt-3">Running Tactmark Analysis Engine. This usually takes 20 to 40 seconds.</p>}
        </div>
      )}
    </div>
  );
}
